/* The Ones Who Stay: gift filters, detail dialogs, reading dialog and the device-local shortlist.
   One instance per section; everything is scoped to the custom element and torn down on disconnect
   so Theme Editor section reloads start clean. The shortlist stores role names only. */
const openDialogs = new Set();

function syncScrollLock() {
  document.documentElement.classList.toggle('pwc-scroll-lock', openDialogs.size > 0);
}

class PxwChickadeeCollection extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const signal = this.abort.signal;

    this.cards = [...this.querySelectorAll('[data-pwc-card]')];
    this.validRoles = this.cards.map((card) => card.dataset.role);
    this.saved = this.readSaved();

    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('close', () => this.onDialogClose(dialog), { signal });
    });

    this.applyFilter('all');
    this.paintSaved();
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearTimeout(this.toastTimer);
    this.querySelectorAll('dialog').forEach((dialog) => openDialogs.delete(dialog));
    syncScrollLock();
  }

  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const piece = target.closest('[data-pwc-open-piece]');
    if (piece) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const dialog = this.querySelector(`[data-pwc-piece-dialog="${piece.dataset.pwcOpenPiece}"]`);
      if (dialog) {
        event.preventDefault();
        this.openDialog(dialog, piece);
      }
      return;
    }

    const filter = target.closest('[data-pwc-filter]');
    if (filter) return this.applyFilter(filter.dataset.pwcFilter);

    const story = target.closest('[data-pwc-open-story]');
    if (story) return this.openDialog(this.querySelector('[data-pwc-story-dialog]'), story);

    const savedOpener = target.closest('[data-pwc-open-saved]');
    if (savedOpener) return this.openDialog(this.querySelector('[data-pwc-saved-dialog]'), savedOpener);

    const close = target.closest('[data-pwc-close]');
    if (close) return close.closest('dialog')?.close();

    const save = target.closest('[data-pwc-save]');
    if (save) return this.toggleSaved(save.dataset.pwcSave);

    const remove = target.closest('[data-pwc-remove]');
    if (remove) return this.removeFromList(remove.dataset.pwcRemove);

    const anchor = target.closest('a[href^="#"]');
    if (anchor) return this.onAnchor(event, anchor);

    if (target instanceof HTMLDialogElement && this.isBackdropClick(event, target)) target.close();
  }

  onAnchor(event, anchor) {
    const destination = this.querySelector(anchor.getAttribute('href'));
    if (!destination) return;
    event.preventDefault();
    if (anchor.dataset.pwcChooseFilter) this.applyFilter(anchor.dataset.pwcChooseFilter);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    destination.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
    destination.setAttribute('tabindex', '-1');
    destination.focus({ preventScroll: true });
  }

  isBackdropClick(event, dialog) {
    const box = dialog.getBoundingClientRect();
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
  }

  openDialog(dialog, opener) {
    if (!dialog || dialog.open) return;
    this.opener = opener;
    dialog.showModal();
    openDialogs.add(dialog);
    syncScrollLock();
  }

  onDialogClose(dialog) {
    openDialogs.delete(dialog);
    syncScrollLock();
    if (this.opener?.isConnected) this.opener.focus();
    this.opener = null;
  }

  applyFilter(group) {
    this.querySelectorAll('[data-pwc-filter]').forEach((button) => {
      const active = button.dataset.pwcFilter === group;
      button.classList.toggle('pwc-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    let visible = 0;
    this.cards.forEach((card) => {
      card.hidden = group !== 'all' && !card.dataset.groups.split(' ').includes(group);
      if (!card.hidden) visible += 1;
    });

    this.querySelector('[data-pwc-grid]')?.classList.toggle('pwc-filtered', group !== 'all');
    const count = this.querySelector('[data-pwc-count]');
    if (count) count.textContent = `${visible} ${visible === 1 ? 'piece' : 'pieces'}`;
  }

  readSaved() {
    try {
      const value = JSON.parse(localStorage.getItem(this.dataset.storageKey) || '[]');
      if (!Array.isArray(value)) return [];
      return [...new Set(value)].filter((role) => this.validRoles.includes(role));
    } catch {
      return [];
    }
  }

  persistSaved() {
    try {
      localStorage.setItem(this.dataset.storageKey, JSON.stringify(this.saved));
      return true;
    } catch {
      return false;
    }
  }

  toggleSaved(role) {
    if (!this.validRoles.includes(role)) return;
    const wasSaved = this.saved.includes(role);
    this.saved = wasSaved ? this.saved.filter((item) => item !== role) : [...this.saved, role];
    const stored = this.persistSaved();
    this.paintSaved();
    if (!stored) this.announce(this.dataset.storageError);
    else this.announce(wasSaved ? this.dataset.removedMessage : this.dataset.savedMessage);
  }

  removeFromList(role) {
    const items = [...this.querySelectorAll('[data-pwc-saved-item]:not([hidden])')];
    const index = items.findIndex((item) => item.dataset.pwcSavedItem === role);
    this.toggleSaved(role);
    const remaining = [...this.querySelectorAll('[data-pwc-saved-item]:not([hidden]) [data-pwc-remove]')];
    const fallback = this.querySelector('[data-pwc-saved-dialog] .pwc-dialog-close');
    (remaining[Math.min(index, remaining.length - 1)] || fallback)?.focus();
  }

  paintSaved() {
    const count = this.saved.length;
    const countEl = this.querySelector('[data-pwc-saved-count]');
    if (countEl) countEl.textContent = String(count);
    this.querySelector('[data-pwc-open-saved]')?.setAttribute('aria-label', `Open saved gift ideas, ${count} saved`);

    this.querySelectorAll('[data-pwc-saved-item]').forEach((item) => {
      item.hidden = !this.saved.includes(item.dataset.pwcSavedItem);
    });
    const empty = this.querySelector('[data-pwc-saved-empty]');
    if (empty) empty.hidden = count > 0;

    this.querySelectorAll('[data-pwc-save]').forEach((button) => {
      const isSaved = this.saved.includes(button.dataset.pwcSave);
      button.setAttribute('aria-pressed', String(isSaved));
      const label = button.querySelector('[data-pwc-save-label]');
      if (label) label.textContent = isSaved ? this.dataset.unsaveLabel : this.dataset.saveLabel;
    });
  }

  announce(message) {
    const openDialog = this.querySelector('dialog[open]');
    const toast = this.querySelector('[data-pwc-toast]');
    if (!toast) return;
    // A modal dialog sits in the top layer, so the status has to live inside it to be seen and heard.
    (openDialog || this).append(toast);
    toast.textContent = message;
    toast.classList.add('pwc-visible');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => toast.classList.remove('pwc-visible'), 3500);
  }
}

if (!customElements.get('pxw-chickadee-collection')) {
  customElements.define('pxw-chickadee-collection', PxwChickadeeCollection);
}
